import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Calendar,
  FileText,
  Phone,
  Mail,
  Building2,
  ChevronRight,
  Plus,
  Search,
  Filter,
  ArrowLeft,
  RefreshCw,
  Loader2,
  MessageSquare,
  Star,
  Trash2,
  LogOut,
  Shield,
  Package,
  MapPin,
  Tag,
  TrendingUp,
  Settings,
  Bell,
  X,
  Webhook,
  UserCog,
  Key,
  Eye,
  EyeOff,
  Target,
  Layout,
  FileSearch,
  PhoneCall,
  ClipboardList
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar as CalendarUI } from '../components/ui/calendar';
import { AdminLogin } from './AdminLogin';
import { NotificationToggle } from './NotificationToggle';
import { WebhookDashboard } from './admin/WebhookDashboard';
import { LeadsCRM } from './admin/LeadsCRM';
import { CalendarManager } from './admin/CalendarManager';
import { AdminUsersManager } from './admin/AdminUsersManager';
import { DispatchSync } from './admin/DispatchSync';
import { CredentialManager } from './admin/CredentialManager';
import { MondayCRM } from './admin/MondayCRM';
import { EnhancedContractBuilder } from './admin/EnhancedContractBuilder';
import { Whiteboard } from './admin/Whiteboard';
import { DocumentOCR } from './admin/DocumentOCR';
import { PhoneSystem } from './admin/PhoneSystem';
import { InsuranceStateManager } from './admin/InsuranceStateManager';
import { PartnerSandboxTester } from './admin/PartnerSandboxTester';
import { InsuranceBillingDashboard } from './admin/InsuranceBillingDashboard';
import { MedexConnectHub } from './admin/MedexConnectHub';
import { WorkOrders } from './admin/WorkOrders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with auth interceptor
const createAuthAxios = () => {
  const instance = axios.create();
  
  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
  
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminInfo');
        window.location.reload();
      }
      return Promise.reject(error);
    }
  );
  
  return instance;
};

const authAxios = createAuthAxios();

const statusColors = {
  new: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'New' },
  contacted: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', label: 'Contacted' },
  in_review: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'In Review' },
  credentialing: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Credentialing' },
  approved: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Approved' },
  rejected: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Rejected' },
};

const priorityColors = {
  low: { bg: 'bg-slate-500/20', text: 'text-slate-400' },
  medium: { bg: 'bg-yellow-500/20', text: 'text-yellow-400' },
  high: { bg: 'bg-red-500/20', text: 'text-red-400' },
};

export const AdminCRM = ({ onBack }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminInfo, setAdminInfo] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [stats, setStats] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const initial = params.get('view');
      if (initial) return initial;
    }
    return 'list';
  }); // list, detail, calendar, products, settings, workorders
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [newNote, setNewNote] = useState('');
  const [schedulingCall, setSchedulingCall] = useState(false);
  const [callData, setCallData] = useState({ title: '', scheduled_time: '', duration_minutes: 30, notes: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { type: 'app' | 'all', id?: string }
  const [isDeleting, setIsDeleting] = useState(false);
  // Product signups state
  const [productSignups, setProductSignups] = useState([]);
  const [productStats, setProductStats] = useState(null);
  const [productFilter, setProductFilter] = useState('');
  // Settings state
  const [notificationEmails, setNotificationEmails] = useState([]);
  const [newEmailInput, setNewEmailInput] = useState('');
  const [newEmailName, setNewEmailName] = useState('');
  // Password change modal state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [changingPassword, setChangingPassword] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const storedAdminInfo = localStorage.getItem('adminInfo');
    
    if (token && storedAdminInfo) {
      // Verify token is still valid
      authAxios.get(`${API}/admin/me`)
        .then(response => {
          setAdminInfo(response.data);
          setIsAuthenticated(true);
        })
        .catch(() => {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminInfo');
          setIsAuthenticated(false);
        })
        .finally(() => setCheckingAuth(false));
    } else {
      setCheckingAuth(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [filterStatus, isAuthenticated]);

  const handleLoginSuccess = (admin) => {
    setAdminInfo(admin);
    setIsAuthenticated(true);
    // Check if password change is required
    if (admin.must_change_password) {
      setShowPasswordChange(true);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await authAxios.post(`${API}/admin/users/change-password`, {
        current_password: adminInfo?.must_change_password ? null : passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password changed successfully!');
      setShowPasswordChange(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      // Update adminInfo to reflect password change
      setAdminInfo({ ...adminInfo, must_change_password: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminInfo');
    setIsAuthenticated(false);
    setAdminInfo(null);
    toast.success('Logged out successfully');
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appsRes, statsRes, calRes] = await Promise.all([
        authAxios.get(`${API}/partners/applications${filterStatus ? `?status=${filterStatus}` : ''}`),
        authAxios.get(`${API}/partners/stats`),
        authAxios.get(`${API}/partners/calendar`)
      ]);
      setApplications(appsRes.data);
      setStats(statsRes.data);
      setCalendarEvents(calRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductSignups = async () => {
    setLoading(true);
    try {
      const [signupsRes, statsRes] = await Promise.all([
        authAxios.get(`${API}/products/signups${productFilter ? `?product=${productFilter}` : ''}`),
        authAxios.get(`${API}/products/signups/stats`)
      ]);
      setProductSignups(signupsRes.data);
      setProductStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching product signups:', error);
      toast.error('Failed to load product signups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && view === 'products') {
      fetchProductSignups();
    }
  }, [view, productFilter, isAuthenticated]);

  // Fetch notification emails for settings
  const fetchNotificationEmails = async () => {
    try {
      const res = await authAxios.get(`${API}/admin/settings/notification-emails`);
      setNotificationEmails(res.data);
    } catch (error) {
      console.error('Error fetching notification emails:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated && view === 'settings') {
      fetchNotificationEmails();
    }
  }, [view, isAuthenticated]);

  const addNotificationEmail = async () => {
    if (!newEmailInput || !newEmailInput.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    try {
      await authAxios.post(`${API}/admin/settings/notification-emails`, {
        email: newEmailInput,
        name: newEmailName || null,
        notify_signups: true,
        notify_applications: true
      });
      toast.success('Email added');
      setNewEmailInput('');
      setNewEmailName('');
      fetchNotificationEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add email');
    }
  };

  const removeNotificationEmail = async (email) => {
    try {
      await authAxios.delete(`${API}/admin/settings/notification-emails/${encodeURIComponent(email)}`);
      toast.success('Email removed');
      fetchNotificationEmails();
    } catch (error) {
      toast.error('Failed to remove email');
    }
  };

  const updateStatus = async (appId, newStatus) => {
    try {
      await authAxios.patch(`${API}/partners/applications/${appId}/status`, { status: newStatus });
      toast.success('Status updated');
      fetchData();
      if (selectedApp?.id === appId) {
        setSelectedApp(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const updatePriority = async (appId, newPriority) => {
    try {
      await authAxios.patch(`${API}/partners/applications/${appId}/status`, { priority: newPriority });
      toast.success('Priority updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update priority');
    }
  };

  const addNote = async (appId) => {
    if (!newNote.trim()) return;
    try {
      await authAxios.post(`${API}/partners/applications/${appId}/notes`, { content: newNote });
      toast.success('Note added');
      setNewNote('');
      // Refresh selected app
      const res = await authAxios.get(`${API}/partners/applications/${appId}`);
      setSelectedApp(res.data);
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const scheduleCall = async (appId) => {
    if (!callData.title || !callData.scheduled_time) {
      toast.error('Please fill in call details');
      return;
    }
    try {
      await authAxios.post(`${API}/partners/applications/${appId}/schedule`, {
        ...callData,
        scheduled_time: new Date(callData.scheduled_time).toISOString()
      });
      toast.success('Call scheduled');
      setSchedulingCall(false);
      setCallData({ title: '', scheduled_time: '', duration_minutes: 30, notes: '' });
      fetchData();
      const res = await authAxios.get(`${API}/partners/applications/${appId}`);
      setSelectedApp(res.data);
    } catch (error) {
      toast.error('Failed to schedule call');
    }
  };

  const deleteApplication = async (appId) => {
    setIsDeleting(true);
    try {
      await authAxios.delete(`${API}/partners/applications/${appId}`);
      toast.success('Application deleted');
      setDeleteConfirm(null);
      setSelectedApp(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete application');
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteAllApplications = async () => {
    setIsDeleting(true);
    try {
      await authAxios.delete(`${API}/partners/applications?confirm=DELETE_ALL`);
      toast.success('All applications deleted');
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete applications');
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredApps = applications.filter(app => 
    app.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    app.contact_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    app.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-neon-cyan animate-spin" />
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} onBack={onBack} />;
  }

  if (loading && !applications.length) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-neon-cyan animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="admin-crm">
      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="glass-card p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">
                  {deleteConfirm.type === 'all' ? 'Delete All Applications?' : 'Delete Application?'}
                </h3>
              </div>
              <p className="text-slate-400 mb-6">
                {deleteConfirm.type === 'all' 
                  ? 'This will permanently delete ALL partner applications. This action cannot be undone.'
                  : 'This will permanently delete this application and all associated data. This action cannot be undone.'
                }
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 btn-secondary"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteConfirm.type === 'all' ? deleteAllApplications() : deleteApplication(deleteConfirm.id)}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
                  disabled={isDeleting}
                  data-testid="confirm-delete-button"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      <span>Delete</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <button
            onClick={onBack}
            className="inline-flex items-center text-slate-400 hover:text-white mb-2 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Site
          </button>
          <h1 className="text-3xl font-heading font-bold text-white">Partner CRM</h1>
          <p className="text-slate-400">Manage partner applications and credentialing</p>
        </div>
        <div className="flex items-center space-x-4">
          {/* Admin Info */}
          <div className="hidden sm:flex items-center space-x-3 px-4 py-2 bg-white/5 rounded-lg">
            <Shield className="w-4 h-4 text-neon-cyan" />
            <span className="text-sm text-slate-300">{adminInfo?.name || adminInfo?.email}</span>
          </div>
          
          {/* Push Notification Toggle */}
          <NotificationToggle 
            userType="admin" 
            userId={adminInfo?.id} 
            email={adminInfo?.email} 
          />
          
          {/* Refresh Button */}
          <button
            onClick={fetchData}
            className="btn-secondary flex items-center space-x-2"
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          
          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            title="Logout"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatCard icon={Users} label="Total Applications" value={stats.total} />
          <StatCard icon={Plus} label="New This Week" value={stats.new_this_week} color="cyan" />
          <StatCard icon={Clock} label="In Review" value={stats.by_status?.in_review || 0} color="yellow" />
          <StatCard icon={FileText} label="Credentialing" value={stats.by_status?.credentialing || 0} color="purple" />
          <StatCard icon={CheckCircle} label="Approved" value={stats.by_status?.approved || 0} color="green" />
        </div>
      )}

      {/* View Tabs */}
      <div className="flex items-center space-x-4 mb-6 overflow-x-auto">
        <button
          onClick={() => { setView('list'); setSelectedApp(null); }}
          className={`px-4 py-2 rounded-lg transition-colors ${view === 'list' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'}`}
        >
          Applications
        </button>
        <button
          onClick={() => setView('products')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${view === 'products' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'}`}
        >
          <Package className="w-4 h-4" />
          <span>Product Signups</span>
        </button>
        <button
          onClick={() => setView('leads')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${view === 'leads' ? 'bg-neon-green/20 text-neon-green' : 'text-slate-400 hover:text-white'}`}
        >
          <Building2 className="w-4 h-4" />
          <span>Leads</span>
        </button>
        <button
          onClick={() => setView('pipeline')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${view === 'pipeline' ? 'bg-neon-cyan/20 text-neon-cyan' : 'text-slate-400 hover:text-white'}`}
        >
          <Target className="w-4 h-4" />
          <span>Deals Pipeline</span>
        </button>
        <button
          onClick={() => setView('whiteboard')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${view === 'whiteboard' ? 'bg-amber-500/20 text-amber-400' : 'text-slate-400 hover:text-white'}`}
        >
          <Layout className="w-4 h-4" />
          <span>Whiteboard</span>
        </button>
        <button
          onClick={() => setView('webhooks')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${view === 'webhooks' ? 'bg-neon-cyan/20 text-neon-cyan' : 'text-slate-400 hover:text-white'}`}
        >
          <Webhook className="w-4 h-4" />
          <span>Webhooks</span>
        </button>
        <button
          onClick={() => setView('calendar')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${view === 'calendar' ? 'bg-neon-purple/20 text-neon-purple' : 'text-slate-400 hover:text-white'}`}
        >
          <Calendar className="w-4 h-4" />
          <span>Calendar</span>
        </button>
        <button
          onClick={() => setView('settings')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${view === 'settings' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'}`}
        >
          <Settings className="w-4 h-4" />
          <span>Settings</span>
        </button>
        <button
          onClick={() => setView('users')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${view === 'users' ? 'bg-amber-500/20 text-amber-400' : 'text-slate-400 hover:text-white'}`}
        >
          <UserCog className="w-4 h-4" />
          <span>Admin Users</span>
        </button>
        <button
          onClick={() => setView('contracts')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${view === 'contracts' ? 'bg-neon-green/20 text-neon-green' : 'text-slate-400 hover:text-white'}`}
        >
          <FileText className="w-4 h-4" />
          <span>Contracts</span>
        </button>
        <button
          onClick={() => setView('dispatch')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${view === 'dispatch' ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-400 hover:text-white'}`}
        >
          <Shield className="w-4 h-4" />
          <span>Dispatch Sync</span>
        </button>
        <button
          onClick={() => setView('credentials')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${view === 'credentials' ? 'bg-purple-500/20 text-purple-400' : 'text-slate-400 hover:text-white'}`}
        >
          <Key className="w-4 h-4" />
          <span>Credentials</span>
        </button>
        <button
          onClick={() => setView('ocr')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${view === 'ocr' ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400 hover:text-white'}`}
        >
          <FileSearch className="w-4 h-4" />
          <span>Document OCR</span>
        </button>
        <button
          onClick={() => setView('phone')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${view === 'phone' ? 'bg-orange-500/20 text-orange-400' : 'text-slate-400 hover:text-white'}`}
        >
          <PhoneCall className="w-4 h-4" />
          <span>Phone System</span>
        </button>
        <button
          onClick={() => setView('insurance')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${view === 'insurance' ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-400 hover:text-white'}`}
        >
          <Shield className="w-4 h-4" />
          <span>Insurance States</span>
        </button>
        <button
          onClick={() => setView('partner-sandbox')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${view === 'partner-sandbox' ? 'bg-yellow-500/20 text-yellow-400' : 'text-slate-400 hover:text-white'}`}
          data-testid="partner-sandbox-tab"
        >
          <Target className="w-4 h-4" />
          <span>Partner API</span>
        </button>
        <button
          onClick={() => setView('billing')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${view === 'billing' ? 'bg-green-500/20 text-green-400' : 'text-slate-400 hover:text-white'}`}
          data-testid="insurance-billing-tab"
        >
          <TrendingUp className="w-4 h-4" />
          <span>Billing Suite</span>
        </button>
        <button
          onClick={() => setView('connect')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${view === 'connect' ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-400 hover:text-white'}`}
          data-testid="medex-connect-tab"
        >
          <MessageSquare className="w-4 h-4" />
          <span>Medex Connect</span>
        </button>
        <button
          onClick={() => setView('workorders')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${view === 'workorders' ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-400 hover:text-white'}`}
          data-testid="workorders-tab"
        >
          <ClipboardList className="w-4 h-4" />
          <span>Work Orders</span>
        </button>
      </div>

      {view === 'leads' && (
        <LeadsCRM />
      )}

      {view === 'pipeline' && (
        <MondayCRM />
      )}

      {view === 'whiteboard' && (
        <Whiteboard />
      )}

      {view === 'webhooks' && (
        <WebhookDashboard />
      )}

      {view === 'calendar' && (
        <CalendarManager />
      )}

      {view === 'list' && !selectedApp && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="Search applications..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 input-dark"
                />
              </div>
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input-dark"
            >
              <option value="">All Statuses</option>
              {Object.entries(statusColors).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
            {applications.length > 0 && (
              <button
                onClick={() => setDeleteConfirm({ type: 'all' })}
                className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors flex items-center space-x-2"
                data-testid="delete-all-button"
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">Delete All</span>
              </button>
            )}
          </div>

          {/* Applications List */}
          <div className="space-y-3">
            {filteredApps.length === 0 ? (
              <div className="glass-card p-8 text-center">
                <p className="text-slate-400">No applications found</p>
              </div>
            ) : (
              filteredApps.map((app) => (
                <motion.div
                  key={app.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-card p-4 hover:border-white/20 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div 
                      className="flex items-center space-x-4 flex-1 cursor-pointer"
                      onClick={() => setSelectedApp(app)}
                    >
                      <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-slate-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{app.company_name}</h3>
                        <p className="text-sm text-slate-400">{app.contact_name} • {app.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[app.status]?.bg} ${statusColors[app.status]?.text}`}>
                        {statusColors[app.status]?.label}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs ${priorityColors[app.priority]?.bg} ${priorityColors[app.priority]?.text}`}>
                        {app.priority}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm({ type: 'app', id: app.id });
                        }}
                        className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        data-testid={`delete-app-${app.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <ChevronRight 
                        className="w-5 h-5 text-slate-500 cursor-pointer" 
                        onClick={() => setSelectedApp(app)}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </>
      )}

      {view === 'list' && selectedApp && (
        <ApplicationDetail
          app={selectedApp}
          onBack={() => setSelectedApp(null)}
          onStatusChange={(status) => updateStatus(selectedApp.id, status)}
          onPriorityChange={(priority) => updatePriority(selectedApp.id, priority)}
          newNote={newNote}
          setNewNote={setNewNote}
          onAddNote={() => addNote(selectedApp.id)}
          schedulingCall={schedulingCall}
          setSchedulingCall={setSchedulingCall}
          callData={callData}
          setCallData={setCallData}
          onScheduleCall={() => scheduleCall(selectedApp.id)}
          onDelete={() => setDeleteConfirm({ type: 'app', id: selectedApp.id })}
        />
      )}

      {view === 'products' && (
        <ProductSignupsView 
          signups={productSignups}
          stats={productStats}
          loading={loading}
          productFilter={productFilter}
          setProductFilter={setProductFilter}
          onRefresh={fetchProductSignups}
        />
      )}

      {view === 'settings' && (
        <div className="space-y-6">
          {/* Notification Emails */}
          <div className="glass-card p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-neon-cyan/20 flex items-center justify-center">
                <Bell className="w-5 h-5 text-neon-cyan" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Notification Emails</h3>
                <p className="text-sm text-slate-400">Manage who receives notifications for new signups and applications</p>
              </div>
            </div>

            {/* Add Email Form */}
            <div className="flex flex-wrap gap-3 mb-6">
              <input
                type="email"
                placeholder="Email address"
                value={newEmailInput}
                onChange={(e) => setNewEmailInput(e.target.value)}
                className="flex-1 min-w-[200px] input-dark"
              />
              <input
                type="text"
                placeholder="Name (optional)"
                value={newEmailName}
                onChange={(e) => setNewEmailName(e.target.value)}
                className="w-40 input-dark"
              />
              <button
                onClick={addNotificationEmail}
                className="btn-primary flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Add</span>
              </button>
            </div>

            {/* Email List */}
            <div className="space-y-2">
              {notificationEmails.length === 0 ? (
                <div className="p-4 bg-white/5 rounded-lg text-center">
                  <Mail className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">No notification emails configured</p>
                  <p className="text-slate-600 text-xs mt-1">Add an email to receive alerts for new signups</p>
                </div>
              ) : (
                notificationEmails.map((item) => (
                  <div key={item.email} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Mail className="w-4 h-4 text-slate-500" />
                      <div>
                        <span className="text-white">{item.email}</span>
                        {item.name && <span className="text-slate-500 text-sm ml-2">({item.name})</span>}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center space-x-1 text-xs">
                        {item.notify_signups && (
                          <span className="px-2 py-0.5 bg-neon-cyan/20 text-neon-cyan rounded">Signups</span>
                        )}
                        {item.notify_applications && (
                          <span className="px-2 py-0.5 bg-neon-purple/20 text-neon-purple rounded">Apps</span>
                        )}
                      </div>
                      <button
                        onClick={() => removeNotificationEmail(item.email)}
                        className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Admin Info */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Admin Account</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <span className="text-slate-400">Email</span>
                <span className="text-white">{adminInfo?.email}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <span className="text-slate-400">Name</span>
                <span className="text-white">{adminInfo?.name || 'Not set'}</span>
              </div>
              <button
                onClick={() => setShowPasswordChange(true)}
                className="w-full mt-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white flex items-center justify-center space-x-2 transition-colors"
              >
                <Key className="w-4 h-4" />
                <span>Change Password</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {view === 'users' && (
        <AdminUsersManager currentAdmin={adminInfo} />
      )}

      {view === 'contracts' && (
        <EnhancedContractBuilder />
      )}

      {view === 'dispatch' && (
        <DispatchSync authToken={localStorage.getItem('adminToken')} />
      )}

      {view === 'credentials' && (
        <CredentialManager />
      )}

      {view === 'ocr' && (
        <DocumentOCR />
      )}

      {view === 'phone' && (
        <PhoneSystem />
      )}

      {view === 'insurance' && (
        <InsuranceStateManager />
      )}

      {view === 'partner-sandbox' && (
        <PartnerSandboxTester />
      )}

      {view === 'billing' && (
        <InsuranceBillingDashboard />
      )}

      {view === 'connect' && (
        <MedexConnectHub />
      )}

      {view === 'workorders' && (
        <WorkOrders />
      )}

      {/* Password Change Modal */}
      <AnimatePresence>
        {showPasswordChange && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => !adminInfo?.must_change_password && setShowPasswordChange(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-800 rounded-2xl p-6 w-full max-w-md mx-4 border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-3 rounded-full bg-amber-500/20">
                    <Key className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Change Password</h3>
                    {adminInfo?.must_change_password && (
                      <p className="text-sm text-amber-400">Password change required</p>
                    )}
                  </div>
                </div>
                {!adminInfo?.must_change_password && (
                  <button onClick={() => setShowPasswordChange(false)} className="text-slate-400 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
              
              <form onSubmit={handlePasswordChange} className="space-y-4">
                {!adminInfo?.must_change_password && (
                  <div>
                    <label className="block text-sm text-slate-300 mb-1">Current Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords.current ? 'text' : 'password'}
                        value={passwordForm.current_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                        className="w-full px-4 py-2.5 pr-12 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-neon-cyan/50 focus:outline-none"
                        placeholder="Enter current password"
                        required={!adminInfo?.must_change_password}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                      >
                        {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm text-slate-300 mb-1">New Password</label>
                  <div className="relative">
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                      className="w-full px-4 py-2.5 pr-12 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-neon-cyan/50 focus:outline-none"
                      placeholder="Min 6 characters"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                    >
                      {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Confirm New Password</label>
                  <div className="relative">
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                      className="w-full px-4 py-2.5 pr-12 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-neon-cyan/50 focus:outline-none"
                      placeholder="Confirm new password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                    >
                      {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={changingPassword}
                  className="w-full py-3 bg-neon-cyan rounded-lg font-semibold text-slate-900 hover:bg-neon-cyan/80 disabled:opacity-50 flex items-center justify-center"
                >
                  {changingPassword ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Change Password'}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color = 'white' }) => {
  const colorClasses = {
    white: 'text-white',
    cyan: 'text-neon-cyan',
    yellow: 'text-yellow-400',
    purple: 'text-purple-400',
    green: 'text-neon-green',
  };
  
  return (
    <div className="glass-card p-4">
      <Icon className={`w-5 h-5 ${colorClasses[color]} mb-2`} />
      <div className={`text-2xl font-heading font-bold ${colorClasses[color]}`}>{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
};

const ApplicationDetail = ({ 
  app, 
  onBack, 
  onStatusChange, 
  onPriorityChange,
  newNote,
  setNewNote,
  onAddNote,
  schedulingCall,
  setSchedulingCall,
  callData,
  setCallData,
  onScheduleCall,
  onDelete
}) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="inline-flex items-center text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to List
        </button>
        <button
          onClick={onDelete}
          className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors flex items-center space-x-2"
          data-testid="delete-detail-button"
        >
          <Trash2 className="w-4 h-4" />
          <span>Delete Application</span>
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="glass-card p-6">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-heading font-bold text-white">{app.company_name}</h2>
                <p className="text-slate-400">{app.company_type?.replace('_', ' ')}</p>
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={app.status}
                  onChange={(e) => onStatusChange(e.target.value)}
                  className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[app.status]?.bg} ${statusColors[app.status]?.text} border-0 cursor-pointer`}
                >
                  {Object.entries(statusColors).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
                <select
                  value={app.priority}
                  onChange={(e) => onPriorityChange(e.target.value)}
                  className={`px-2 py-1 rounded text-sm ${priorityColors[app.priority]?.bg} ${priorityColors[app.priority]?.text} border-0 cursor-pointer`}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <Mail className="w-4 h-4 text-slate-500" />
                <span className="text-slate-300">{app.email}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-4 h-4 text-slate-500" />
                <span className="text-slate-300">{app.phone || 'Not provided'}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Users className="w-4 h-4 text-slate-500" />
                <span className="text-slate-300">{app.contact_name}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Building2 className="w-4 h-4 text-slate-500" />
                <span className="text-slate-300">{app.service_area || 'Not specified'}</span>
              </div>
            </div>

            {app.services_interested?.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-slate-400 mb-2">Services Interested</h4>
                <div className="flex flex-wrap gap-2">
                  {app.services_interested.map((service) => (
                    <span key={service} className="px-3 py-1 bg-neon-cyan/10 text-neon-cyan text-sm rounded-full">
                      {service}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {app.message && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-slate-400 mb-2">Message</h4>
                <p className="text-slate-300 bg-black/20 p-4 rounded-lg">{app.message}</p>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <MessageSquare className="w-5 h-5 mr-2 text-neon-cyan" />
              Notes & Activity
            </h3>
            
            <div className="flex space-x-3 mb-4">
              <input
                type="text"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note..."
                className="flex-1 input-dark"
                onKeyPress={(e) => e.key === 'Enter' && onAddNote()}
              />
              <button onClick={onAddNote} className="btn-primary px-4">Add</button>
            </div>

            <div className="space-y-3 max-h-64 overflow-y-auto">
              {app.notes?.length > 0 ? (
                app.notes.slice().reverse().map((note) => (
                  <div key={note.id} className="p-3 bg-black/20 rounded-lg">
                    <p className="text-slate-300 text-sm">{note.content}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {note.author} • {new Date(note.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-sm">No notes yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Schedule Call */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-neon-cyan" />
              Schedule Call
            </h3>
            
            {!schedulingCall ? (
              <button
                onClick={() => setSchedulingCall(true)}
                className="w-full btn-secondary flex items-center justify-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Schedule New Call</span>
              </button>
            ) : (
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Call title"
                  value={callData.title}
                  onChange={(e) => setCallData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full input-dark"
                />
                <input
                  type="datetime-local"
                  value={callData.scheduled_time}
                  onChange={(e) => setCallData(prev => ({ ...prev, scheduled_time: e.target.value }))}
                  className="w-full input-dark"
                />
                <select
                  value={callData.duration_minutes}
                  onChange={(e) => setCallData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) }))}
                  className="w-full input-dark"
                >
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">1 hour</option>
                </select>
                <textarea
                  placeholder="Notes"
                  value={callData.notes}
                  onChange={(e) => setCallData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full input-dark resize-none"
                  rows={2}
                />
                <div className="flex space-x-2">
                  <button onClick={() => setSchedulingCall(false)} className="flex-1 btn-secondary">Cancel</button>
                  <button onClick={onScheduleCall} className="flex-1 btn-primary">Schedule</button>
                </div>
              </div>
            )}

            {app.scheduled_calls?.length > 0 && (
              <div className="mt-4 space-y-2">
                <h4 className="text-sm font-medium text-slate-400">Upcoming Calls</h4>
                {app.scheduled_calls.map((call) => (
                  <div key={call.id} className="p-3 bg-black/20 rounded-lg">
                    <p className="text-white text-sm font-medium">{call.title}</p>
                    <p className="text-xs text-slate-400">
                      {new Date(call.scheduled_time).toLocaleString()} • {call.duration_minutes} min
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Credentialing Status */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-neon-purple" />
              Credentialing
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">Status</span>
                <span className={`px-2 py-1 rounded text-xs ${
                  app.credentialing_status === 'approved' ? 'bg-green-500/20 text-green-400' :
                  app.credentialing_status === 'under_review' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-slate-500/20 text-slate-400'
                }`}>
                  {app.credentialing_status || 'Not Started'}
                </span>
              </div>
              <div className="text-sm text-slate-500">
                Documents: {app.documents_received?.length || 0} received
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-neon-cyan" />
              Timeline
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Created</span>
                <span className="text-slate-300">{new Date(app.created_at).toLocaleDateString()}</span>
              </div>
              {app.contacted_at && (
                <div className="flex justify-between">
                  <span className="text-slate-400">First Contact</span>
                  <span className="text-slate-300">{new Date(app.contacted_at).toLocaleDateString()}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-400">Last Updated</span>
                <span className="text-slate-300">{new Date(app.updated_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const CalendarView = ({ events, onSelectApp }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  const eventsOnDate = events.filter(e => {
    const eventDate = new Date(e.scheduled_time);
    return eventDate.toDateString() === selectedDate.toDateString();
  });

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Calendar</h3>
        <CalendarUI
          mode="single"
          selected={selectedDate}
          onSelect={(date) => date && setSelectedDate(date)}
          className="rounded-md"
        />
      </div>
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-white mb-4">
          Calls on {selectedDate.toLocaleDateString()}
        </h3>
        {eventsOnDate.length === 0 ? (
          <p className="text-slate-500">No calls scheduled</p>
        ) : (
          <div className="space-y-3">
            {eventsOnDate.map((event) => (
              <div 
                key={event.id} 
                className="p-4 bg-black/20 rounded-lg cursor-pointer hover:bg-black/30 transition-colors"
                onClick={() => onSelectApp(event.partner_id)}
              >
                <h4 className="font-semibold text-white">{event.title}</h4>
                <p className="text-sm text-neon-cyan">{event.company_name}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {new Date(event.scheduled_time).toLocaleTimeString()} • {event.duration_minutes} min
                </p>
                {event.notes && (
                  <p className="text-xs text-slate-500 mt-2">{event.notes}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Product name mappings
const productNames = {
  medex_group_insurance: { name: 'Medex Group Insurance', color: 'purple', icon: Shield },
  medex_pay: { name: 'Medex Pay', color: 'blue', icon: TrendingUp },
  ova_dispatch: { name: 'OVA Dispatch', color: 'cyan', icon: Package },
  nemt_vision: { name: 'NEMT Vision', color: 'green', icon: Package },
  credentialing: { name: 'Credentialing', color: 'cyan', icon: FileText },
};

const ProductSignupsView = ({ signups, stats, loading, productFilter, setProductFilter, onRefresh }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSignup, setExpandedSignup] = useState(null);

  const filteredSignups = signups.filter(s =>
    s.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.state?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading && !signups.length) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-neon-cyan animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Product Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="glass-card p-4">
            <div className="text-2xl font-heading font-bold text-white">{stats.total || 0}</div>
            <div className="text-xs text-slate-500">Total Signups</div>
          </div>
          {Object.entries(stats.by_product || {}).map(([product, count]) => {
            const prodInfo = productNames[product] || { name: product, color: 'white' };
            return (
              <div key={product} className="glass-card p-4">
                <div className={`text-2xl font-heading font-bold text-neon-${prodInfo.color}`}>{count}</div>
                <div className="text-xs text-slate-500">{prodInfo.name}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Insurance by State (if available) */}
      {stats?.insurance_by_state && Object.keys(stats.insurance_by_state).length > 0 && (
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <MapPin className="w-5 h-5 mr-2 text-neon-purple" />
            Founding Providers by State
            <span className="text-xs text-slate-500 ml-2">(20 per state to launch coverage)</span>
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {Object.entries(stats.insurance_by_state).sort((a, b) => b[1] - a[1]).map(([state, count]) => (
              <div key={state} className={`p-3 rounded-lg ${count >= 20 ? 'bg-neon-green/20 border border-neon-green/50' : 'bg-white/5'}`}>
                <div className={`text-lg font-bold ${count >= 20 ? 'text-neon-green' : 'text-white'}`}>{count}</div>
                <div className="text-xs text-slate-400">{state}</div>
                {count >= 20 && <div className="text-xs text-neon-green mt-1">Ready!</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Search signups..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 input-dark"
            />
          </div>
        </div>
        <select
          value={productFilter}
          onChange={(e) => setProductFilter(e.target.value)}
          className="input-dark"
        >
          <option value="">All Products</option>
          {Object.entries(productNames).map(([key, val]) => (
            <option key={key} value={key}>{val.name}</option>
          ))}
        </select>
        <button onClick={onRefresh} className="btn-secondary flex items-center space-x-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Signups List */}
      <div className="space-y-3">
        {filteredSignups.length === 0 ? (
          <div className="glass-card p-8 text-center">
            <Package className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No product signups found</p>
          </div>
        ) : (
          filteredSignups.map((signup) => {
            const prodInfo = productNames[signup.product] || { name: signup.product, color: 'white' };
            const isExpanded = expandedSignup === signup.id;
            
            return (
              <motion.div
                key={signup.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card p-4 hover:border-white/20 transition-all cursor-pointer"
                onClick={() => setExpandedSignup(isExpanded ? null : signup.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-10 h-10 rounded-lg bg-neon-${prodInfo.color}/20 flex items-center justify-center`}>
                      <Package className={`w-5 h-5 text-neon-${prodInfo.color}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{signup.company_name}</h3>
                      <p className="text-sm text-slate-400">{signup.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium bg-neon-${prodInfo.color}/20 text-neon-${prodInfo.color}`}>
                      {prodInfo.name}
                    </span>
                    {signup.state && (
                      <span className="px-2 py-1 rounded text-xs bg-white/5 text-slate-400">
                        {signup.state}
                      </span>
                    )}
                    <ChevronRight className={`w-5 h-5 text-slate-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </div>
                </div>

                {/* Expanded Details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-4 mt-4 border-t border-white/10 grid md:grid-cols-2 gap-4">
                        <div className="space-y-2 text-sm">
                          {signup.contact_name && (
                            <div className="flex items-center space-x-2">
                              <Users className="w-4 h-4 text-slate-500" />
                              <span className="text-slate-300">{signup.contact_name}</span>
                            </div>
                          )}
                          {signup.phone && (
                            <div className="flex items-center space-x-2">
                              <Phone className="w-4 h-4 text-slate-500" />
                              <span className="text-slate-300">{signup.phone}</span>
                            </div>
                          )}
                          {signup.fleet_size && (
                            <div className="flex items-center space-x-2">
                              <Package className="w-4 h-4 text-slate-500" />
                              <span className="text-slate-300">Fleet: {signup.fleet_size}</span>
                            </div>
                          )}
                          {signup.monthly_trips && (
                            <div className="flex items-center space-x-2">
                              <TrendingUp className="w-4 h-4 text-slate-500" />
                              <span className="text-slate-300">Monthly Trips: {signup.monthly_trips}</span>
                            </div>
                          )}
                        </div>
                        <div>
                          {signup.tags && signup.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {signup.tags.map((tag, i) => (
                                <span key={i} className="px-2 py-0.5 rounded text-xs bg-white/5 text-slate-500">
                                  <Tag className="w-3 h-3 inline mr-1" />{tag}
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="mt-2 text-xs text-slate-500">
                            Signed up: {new Date(signup.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AdminCRM;
